import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { templateApi } from '../services/api';
import './Upload.css';

function Upload({ isFirstRun = false }) {
    const [file, setFile] = useState(null);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);
    const [dragging, setDragging] = useState(false);
    const fileInputRef = useRef(null);
    const navigate = useNavigate();

    const handleDragOver = (e) => {
        e.preventDefault();
        setDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragging(false);

        const droppedFiles = Array.from(e.dataTransfer.files);
        if (droppedFiles.length > 0) {
            const droppedFile = droppedFiles[0];
            if (validateFile(droppedFile)) {
                setFile(droppedFile);
                if (!name) {
                    setName(droppedFile.name.replace(/\.[^/.]+$/, ""));
                }
            }
        }
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile && validateFile(selectedFile)) {
            setFile(selectedFile);
            if (!name) {
                setName(selectedFile.name.replace(/\.[^/.]+$/, ""));
            }
        }
    };

    const validateFile = (file) => {
        const allowedTypes = [
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/vnd.ms-excel',
            'text/csv'
        ];

        if (!allowedTypes.includes(file.type)) {
            setError('Please select a valid Excel (.xlsx, .xls) or CSV file');
            return false;
        }

        if (file.size > 10 * 1024 * 1024) { // 10MB
            setError('File size must be less than 10MB');
            return false;
        }

        setError(null);
        return true;
    };

    const handleUpload = async () => {
        if (!file) {
            setError('Please select a file');
            return;
        }

        if (!name.trim()) {
            setError('Please enter a name for the template');
            return;
        }

        setUploading(true);
        setError(null);

        try {
            const result = await templateApi.upload(file, name.trim(), description.trim());

            // Redirect to review page to verify extracted logic
            navigate(`/app/templates/${result.template.id}/review`);
        } catch (err) {
            setError(err.message || 'Upload failed');
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="upload-page fade-in">
            <div className="page-header">
                {isFirstRun ? (
                    <>
                        <h1>Create Your First Calculation Template</h1>
                        <p style={{ marginTop: '0.5rem', opacity: 0.8 }}>
                            Upload an Excel file with your calculations. We'll turn it into a template that your team can use.
                        </p>
                    </>
                ) : (
                    <>
                        <h1>Upload Calculation Template</h1>
                        <p style={{ marginTop: '0.5rem', opacity: 0.8 }}>
                            Upload an Excel file to create a new calculation template for your team.
                        </p>
                    </>
                )}
            </div>

            <div className="upload-container">
                {isFirstRun && (
                    <div className="alert alert-info mb-4">
                        <div>
                            <strong>How it works:</strong> Upload your Excel file, and we'll extract the formulas and structure.
                            Your team can then input values and get instant results without editing the spreadsheet.
                        </div>
                    </div>
                )}

                {error && (
                    <div className="alert alert-error mb-4">
                        {error}
                    </div>
                )}

                {/* ... existing upload zone */}

                <div
                    className={`upload-zone ${dragging ? 'dragging' : ''} ${file ? 'has-file' : ''}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                    />

                    {file ? (
                        <div className="file-preview">
                            <span className="file-icon">📄</span>
                            <div className="file-info">
                                <span className="file-name">{file.name}</span>
                                <span className="file-size">
                                    {(file.size / 1024).toFixed(1)} KB
                                </span>
                            </div>
                            <button
                                className="btn btn-sm btn-secondary"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setFile(null);
                                }}
                            >
                                Remove
                            </button>
                        </div>
                    ) : (
                        <>
                            <span className="upload-icon">📤</span>
                            <h3>Drop your Excel file here</h3>
                            <p>or click to browse your computer</p>
                            <p className="text-sm text-muted">
                                Supports Excel (.xlsx, .xls) and CSV files up to 10MB
                            </p>
                        </>
                    )}
                </div>

                {file && (
                    <div className="upload-form">
                        {/* ... existing form fields ... */}
                        <div className="form-group">
                            <label className="form-label">Template Name</label>
                            <input
                                type="text"
                                className="form-input"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="e.g., Sales Commission Calculator"
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label">Description (optional)</label>
                            <textarea
                                className="form-input"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Briefly describe what this calculator does and when to use it"
                                rows={3}
                            />
                        </div>

                        <button
                            className="btn btn-primary btn-lg"
                            onClick={handleUpload}
                            disabled={uploading}
                            style={{ width: '100%' }}
                        >
                            {uploading ? (
                                <>
                                    <span className="spinner"></span>
                                    Processing your file...
                                </>
                            ) : (
                                'Create Template'
                            )}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Upload;
